import { useState, useEffect } from 'react'
import { Label } from '@/components/ui/label'
import { useQuery } from '@apollo/client'
import { GENERATED_IMAGES } from '@/graphql/ads/generated-image-queries'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { useWizard } from '@/components/mediaBuying/adCreatives/AdCreativeWizard'
import { cn } from '@/lib/utils'

const AIImageGeneration = ({ campaignFormat }) => {
    const [isLoading, setIsLoading] = useState(true)
    const isVideoFormat = campaignFormat === 'video'
    const [selected, setSelected] = useState(isVideoFormat ? 'textToVideo' : 'textToImage')
    const [hasGeneratedImages, setHasGeneratedImages] = useState(false)
    const { registerNextGuard, deregisterNextGuard, goToStep, setStepContentData } = useWizard()

    useQuery(GENERATED_IMAGES, {
        fetchPolicy: 'network-only',
        skip: isVideoFormat,
        onCompleted: (data) => {
            const images = data.generatedImages
            setHasGeneratedImages(images.length > 0)
            setIsLoading(false)
        },
    })

    useEffect(() => {
        const guard = registerNextGuard(() => {
            setStepContentData({ selectedOption: selected }, 'uploadAssets')

            if (selected === 'textToVideo') goToStep('aiTextToVideo')
            else if (selected === 'textToImage') goToStep('aiTextToImage')
            else if (selected === 'imageToImage') goToStep('aiImageToImage')
            else goToStep('aiAssetLibrary')

            return false
        })

        return () => deregisterNextGuard(guard)
    }, [selected])

    const handleChange = (value) => setSelected(value)

    if (isVideoFormat) {
        return (
            <>
                <span className="tw:block tw:mb-4 tw:font-semibold">Generation Type</span>
                <RadioGroup className="tw:gap-3" onValueChange={handleChange} defaultValue={selected}>
                    <div className="tw:flex tw:items-center tw:gap-3">
                        <RadioGroupItem value="textToVideo" id="textToVideo" />
                        <Label htmlFor="textToVideo">Generate video from text prompt</Label>
                    </div>
                </RadioGroup>
            </>
        )
    }

    return (
        <>
            <span className="tw:block tw:mb-4 tw:font-semibold">Generation Type</span>
            <RadioGroup className="tw:gap-3" onValueChange={handleChange} defaultValue={selected}>
                <div className="tw:flex tw:items-center tw:gap-3">
                    <RadioGroupItem value="textToImage" id="textToImage" />
                    <Label htmlFor="textToImage">Generate image from text prompt</Label>
                </div>
                <div className="tw:flex tw:items-start tw:gap-3">
                    <RadioGroupItem value="aiAssetLibrary" id="aiAssetLibrary" disabled={!hasGeneratedImages} />
                    <div className="tw:flex tw:flex-col">
                        <Label htmlFor="aiAssetLibrary" className={cn({ 'tw:text-gray-200': !hasGeneratedImages })}>
                            Select from my AI generations library
                        </Label>
                        {!hasGeneratedImages && (
                            <Label className="tw:text-gray-200/70 tw:mt-0.5">
                                Start generating images to enable this option
                            </Label>
                        )}
                    </div>
                </div>
                <div className="tw:flex tw:items-center tw:gap-3">
                    <RadioGroupItem value="imageToImage" id="imageToImage" />
                    <div className="tw:flex tw:flex-col">
                        <Label htmlFor="imageToImage">Generate variations from existing image</Label>
                    </div>
                </div>
            </RadioGroup>
        </>
    )
}

export default AIImageGeneration
